import {
  type UseIntersectionObserverOptions,
  useIntersectionObserver,
} from '@vueuse/core';
import { type ObjectDirective, } from 'vue';

export interface TrackExposeOptions extends UseIntersectionObserverOptions {
  /** 有效曝光停留时长，单位：毫秒(ms)。默认：2000。 */
  stayDuration?: number;
  /** 重复曝光的时间间隔，单位：毫秒(ms)。默认：500。 */
  repeatedInterval?: number;
}

export type BindingValueFunction = IntersectionObserverCallback;

export type BindingValueArray = [BindingValueFunction, TrackExposeOptions];

const DEFAULT_OPTIONS: TrackExposeOptions = {
  threshold: 1,
  stayDuration: 1000,
  repeatedInterval: 500,
  immediate: true,
};

const visibleMemo = new WeakMap<HTMLElement, number>();
const invisibleMemo = new WeakMap<HTMLElement, number>();

const clear = (memo: WeakMap<HTMLElement, number>, el: HTMLElement) => {
  const timer = memo.get(el);

  if (timer) {
    window.clearTimeout(timer);
  }
  memo.delete(el);
};

export const vTrackExpose: ObjectDirective<
  HTMLElement,
  BindingValueFunction | BindingValueArray
> = {
  mounted(el, binding) {
    const [callback, options = {},] = Array.isArray(binding.value)
      ? binding.value
      : [binding.value,];
    const totalOptions: TrackExposeOptions = {
      ...DEFAULT_OPTIONS,
      ...options,
    };
    const { stayDuration, repeatedInterval, } = totalOptions;

    useIntersectionObserver(
      el,
      (entries, observer) => {
        const [{ isIntersecting, },] = entries;

        // 代理 observer，监听观测注销来清空 memo
        const observerProxy = new Proxy(observer, {
          get(target, p, receiver) {
            const value = Reflect.get(target, p, receiver);

            if (p === 'unobserve') {
              return (e: Element) => {
                invisibleMemo.delete(e as HTMLElement);
                visibleMemo.delete(e as HTMLElement);
                return Reflect.apply(value, target, [e,]);
              };
            }
            if (typeof value === 'function') {
              return (...args: unknown[]) => Reflect.apply(value, target, args);
            }
            return value;
          },
        });

        if (isIntersecting) {
          if (invisibleMemo.get(el)) {
            clear(invisibleMemo, el);
          }
          if (visibleMemo.get(el)) {
            // 一直可见的元素不重复曝光
            return;
          }
          if (stayDuration) {
            visibleMemo.set(
              el,
              window.setTimeout(() => {
                callback(entries, observerProxy);
              }, stayDuration)
            );
            return;
          }
          return callback(entries, observerProxy);
        }

        invisibleMemo.set(
          el,
          window.setTimeout(() => {
            clear(visibleMemo, el);
          }, repeatedInterval)
        );
      },
      totalOptions
    );
  },

  beforeUnmount(el) {
    clear(visibleMemo, el);
    clear(invisibleMemo, el);
  },
};

export const vTrackExposeName = 'trackExpose';

export default vTrackExpose;
